import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import SignInNudge from './SignInNudge';
import { authService } from '../services/authService';
import { tagInviteService, TagInvitePreview } from '../services/tagInviteService';

/**
 * /tag/:postId/:tagId/:code — opened from the link an author sent to someone they tagged.
 * Shows who tagged them, asks them to sign in or join, then a single "claim" click attaches the
 * tag to their account. Claiming is a click, not automatic, so a link preview can't use it up.
 */
const TagInviteLanding: React.FC = () => {
  const { postId = '', tagId = '', code = '' } = useParams();
  const navigate = useNavigate();
  const [signedIn, setSignedIn] = useState(() => authService.isAuthenticated());
  const [preview, setPreview] = useState<TagInvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => authService.subscribe(() => setSignedIn(authService.isAuthenticated())), []);

  useEffect(() => {
    let cancelled = false;
    tagInviteService.preview(postId, tagId, code)
      .then(p => { if (!cancelled) setPreview(p); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : "This tag link isn't valid."); });
    return () => { cancelled = true; };
  }, [postId, tagId, code]);

  const handleClaim = async () => {
    setClaiming(true);
    setError(null);
    try {
      const { postId: claimedPostId } = await tagInviteService.claim(postId, tagId, code);
      navigate(`/post/${claimedPostId}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not claim this tag.');
      setClaiming(false);
    }
  };

  if (!preview && !error) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  if (!preview) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', textAlign: 'center', pt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/')}>Go to New Achievement Scuba</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', textAlign: 'center', pt: { xs: 1, sm: 4 } }}>
      <Typography variant="h4" sx={{ fontWeight: 800 }}>
        {preview.authorDisplayName} tagged you
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
        They called you <strong>{preview.taggedName}</strong> in a dive post on New Achievement Scuba — where divers connect.
      </Typography>
      {preview.snippet && (
        <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>“{preview.snippet}”</Typography>
      )}

      {preview.alreadyClaimed ? (
        <Alert severity="info" sx={{ mt: 3 }}>This tag has already been claimed.</Alert>
      ) : signedIn ? (
        <Box sx={{ mt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Button variant="contained" size="large" disabled={claiming} onClick={handleClaim}>
            {claiming ? <CircularProgress size={22} /> : 'Claim this tag and see the post'}
          </Button>
        </Box>
      ) : (
        <SignInNudge
          feature="see the post you were tagged in"
          description={`Create a free account (or sign in) to claim your tag. You'll start out following ${preview.authorDisplayName}.`}
          onSignedIn={() => authService.getUser()}
        />
      )}
    </Box>
  );
};

export default TagInviteLanding;
